import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import Spacings from '~/modules/ui-kit/spacings';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import formMessages from '~/components/form-messages';
import generalMessages from '~/containers/App/messages';
import { useStore } from '@tanstack/react-form';
import messages from './messages';
import { useAppForm } from '~/components/Form';
import { FolderPurpose } from '~/utils/folder-purpose';

const createFormSchema = (intl: IntlShape) =>
  z.object({
    name: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, { message: intl.formatMessage(formMessages.required) }),
    purpose: z.enum(['0', '1', '2', '3', '4']),
    contentExpirationInDays: z.enum(['0', '7', '14', '30']),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

type FolderFormProps = {
  onSubmit: (values: FormValues) => void;
};

export const FolderForm = (props: FolderFormProps) => {
  const intl = useIntl();
  const form = useAppForm({
    defaultValues: {
      name: '',
      contentExpirationInDays: '0' as FormValues['contentExpirationInDays'],
      purpose:
        FolderPurpose.PublicInformation.toString() as FormValues['purpose'],
    },
    validators: {
      onChange: createFormSchema(intl),
    },
    onSubmit: ({ value }) => {
      props.onSubmit(value);
    },
  });
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Stack scale="l">
        <div>
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                label={intl.formatMessage(messages.newFolderName)}
                placeholder={intl.formatMessage(messages.newFolderName)}
              />
            )}
          </form.AppField>
        </div>
        <div>
          <form.AppField name="purpose">
            {(field) => (
              <field.RadioGroupField
                label={<FormattedMessage {...messages.purpose} />}
                options={[
                  {
                    value: FolderPurpose.PublicInformation.toString(),
                    label: (
                      <FormattedMessage
                        {...messages.purposePublicInformation}
                      />
                    ),
                  },
                  {
                    value: FolderPurpose.TeamLeaderInformation.toString(),
                    label: (
                      <FormattedMessage
                        {...messages.purposeTeamLeaderInformation}
                      />
                    ),
                  },
                  {
                    value: FolderPurpose.VipInformation.toString(),
                    label: (
                      <FormattedMessage {...messages.purposeVipInformation} />
                    ),
                  },
                  {
                    value: FolderPurpose.UploadTeamLeader.toString(),
                    label: (
                      <FormattedMessage {...messages.purposeUploadTeamLeader} />
                    ),
                  },
                  {
                    value: FolderPurpose.CoordinationTeamExchange.toString(),
                    label: (
                      <FormattedMessage
                        {...messages.purposeCoordinationTeamExchange}
                      />
                    ),
                  },
                ]}
              />
            )}
          </form.AppField>
        </div>
        <div>
          <form.AppField name="contentExpirationInDays">
            {(field) => (
              <field.RadioGroupField
                label={
                  <>
                    <FormattedMessage {...messages.expiration} />
                    <Typography variant="caption" color="textSecondary">
                      <FormattedMessage {...messages.expirationIsFixed} />
                    </Typography>
                  </>
                }
                options={[
                  {
                    value: '0',
                    label: <FormattedMessage {...messages.expiresNever} />,
                  },
                  {
                    value: '7',
                    label: <FormattedMessage {...messages.expiresAfter7Days} />,
                  },
                  {
                    value: '14',
                    label: (
                      <FormattedMessage {...messages.expiresAfter14Days} />
                    ),
                  },
                  {
                    value: '30',
                    label: (
                      <FormattedMessage {...messages.expiresAfter30Days} />
                    ),
                  },
                ]}
              />
            )}
          </form.AppField>
        </div>
        <div>
          <Button variant="contained" size="large" type="submit">
            {isSubmitting ? (
              <FormattedMessage {...generalMessages.loading} />
            ) : (
              <FormattedMessage {...generalMessages.save} />
            )}
          </Button>
        </div>
      </Spacings.Stack>
    </form>
  );
};

export default FolderForm;
